/* ==========================================================================
   Pups & Paws — theme.js
   All storefront behaviour in one place, one small module per feature.

   Contents
     1. Helpers
     2. Cart: add-to-cart, fly-to-cart animation, cart drawer
     3. Product page: gallery, lightbox, variant sync, sticky add-to-cart
     4. Product page: Cuddle Care Box button + desktop placement
     5. Build-your-own-box builder (+ preselection from product pages)
     6. Testimonial carousel
     7. Welcome popup
     8. Product card background tint (from packaging colour)
     9. Ambient motion: scroll parallax
    10. Page-ready behaviour: mobile menu, header shadow, scroll reveal,
        pointer parallax, quantity buttons, shop-all search & filters
    11. Boot (order matters — see note there)

   Settings that switch behaviour on/off live in the theme editor; this file
   only reads data-* attributes and classes the sections output.
   Creator Club page behaviour lives in assets/creator-club.js.
   Breeze cart compatibility lives in assets/cart-bridge.js (loads first).
   ========================================================================== */
(() => {
  'use strict';

  /* ------------------------------------------------------------------------
     1. Helpers
     ------------------------------------------------------------------------ */
  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => [...root.querySelectorAll(selector)];
  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const motionTurnedOff = () => document.body.classList.contains('pp-motion-off');

  const formatMoney = (cents) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'INR',
      }).format(cents / 100);
    } catch (e) {
      return `₹${(cents / 100).toFixed(2)}`;
    }
  };

  const escapeHtml = (value) =>
    String(value ?? '').replace(/[&<>'"]/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[char]));

  const lockScroll = (locked) => document.body.classList.toggle('pp-no-scroll', locked);

  /* ------------------------------------------------------------------------
     2. Cart
     ------------------------------------------------------------------------ */

  // Product image "flies" into the care-bag icon. Also used by cart-bridge.js
  // when Breeze handles the add-to-cart, hence exposed on window.
  function flyProductToCart(form) {
    if (prefersReducedMotion()) return Promise.resolve();
    const cart = $('[data-cart-open]');
    const productRoot = form.closest('.pp-card,[data-product-root],.pp-product-card,.product-card,.pp-byob');
    const image = productRoot?.querySelector(
      '[data-pdp-main],.pp-card__media img,.pp-product-card__image img,.product-card img,.pp-byob__product.is-selected img'
    );
    if (!cart || !image) return Promise.resolve();

    const from = image.getBoundingClientRect();
    const to = cart.getBoundingClientRect();
    if (!from.width || !from.height || !to.width || !to.height) return Promise.resolve();

    const flyer = image.cloneNode(true);
    flyer.removeAttribute('loading');
    flyer.removeAttribute('id');
    flyer.removeAttribute('data-pdp-main');
    flyer.setAttribute('aria-hidden', 'true');
    flyer.className = 'pp-cart-flyer';
    Object.assign(flyer.style, {
      left: `${from.left}px`,
      top: `${from.top}px`,
      width: `${from.width}px`,
      height: `${from.height}px`,
    });
    document.body.appendChild(flyer);

    const dx = to.left + to.width / 2 - (from.left + from.width / 2);
    const dy = to.top + to.height / 2 - (from.top + from.height / 2);
    const endScale = Math.max(0.035, Math.min(0.16, Math.min(to.width / from.width, to.height / from.height) * 0.72));
    const animation = flyer.animate(
      [
        { transform: 'translate3d(0,0,0) scale(1) rotate(0deg)', opacity: 1, offset: 0 },
        { transform: `translate3d(${dx * 0.18}px,${dy * 0.08 - 22}px,0) scale(.88) rotate(-2deg)`, opacity: 1, offset: 0.2 },
        { transform: `translate3d(${dx * 0.68}px,${dy * 0.48 - 72}px,0) scale(.42) rotate(5deg)`, opacity: 0.96, offset: 0.66 },
        { transform: `translate3d(${dx}px,${dy}px,0) scale(${endScale}) rotate(9deg)`, opacity: 0.15, offset: 1 },
      ],
      { duration: 760, easing: 'cubic-bezier(.22,.8,.24,1)', fill: 'forwards' }
    );

    return animation.finished
      .catch(() => {})
      .then(() => {
        flyer.remove();
        cart.classList.remove('pp-cart-received');
        void cart.offsetWidth; // restart the bounce animation
        cart.classList.add('pp-cart-received');
        window.setTimeout(() => cart.classList.remove('pp-cart-received'), 520);
      });
  }
  window.ppFlyToCart = flyProductToCart;

  function cartItemHtml(item, index) {
    const properties = Object.entries(item.properties || {})
      .filter(([key, value]) => value && key.charAt(0) !== '_')
      .map(([key, value]) => `<small class="pp-cart-item__property"><b>${escapeHtml(key)}:</b> ${escapeHtml(value)}</small>`)
      .join('');
    const variant = item.variant_title && item.variant_title !== 'Default Title' ? `<small>${item.variant_title}</small>` : '';
    const line = index + 1;
    return `<div class="pp-cart-item"><a href="${item.url}" class="pp-cart-item__image">${item.image ? `<img src="${item.image}" alt="">` : ''}</a><div><a href="${item.url}"><strong>${item.product_title}</strong></a>${variant}${properties}<span>${formatMoney(item.final_line_price)}</span><div class="pp-cart-item__qty"><button data-cart-change="${line}" data-delta="-1">−</button><b>${item.quantity}</b><button data-cart-change="${line}" data-delta="1">+</button><button class="pp-cart-item__remove" data-cart-remove="${line}">Remove</button></div></div></div>`;
  }

  async function renderCart(open = true) {
    const drawer = $('[data-cart-drawer]');
    if (!drawer) return;
    const body = $('[data-cart-drawer-body]', drawer);
    const foot = $('[data-cart-drawer-foot]', drawer);
    const cart = await fetch('/cart.js').then((r) => r.json());

    if (!cart.item_count) {
      body.innerHTML =
        '<div class="pp-cart-empty"><b>Care bag is empty.</b><p>Time to add something cuddle-approved.</p><a class="pp-btn pp-btn--orange" href="/collections/all">Shop all</a></div>';
      foot.hidden = true;
    } else {
      body.innerHTML = cart.items.map(cartItemHtml).join('');
      foot.hidden = false;
      $('[data-cart-subtotal]', foot).textContent = formatMoney(cart.total_price);
    }
    $$('[data-cart-count]').forEach((el) => (el.textContent = cart.item_count));
    if (open) {
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      lockScroll(true);
    }
  }

  const changeLine = (line, quantity) =>
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line, quantity }),
    });

  function initCart() {
    document.addEventListener('click', async (event) => {
      if (event.target.closest('[data-cart-open]')) {
        event.preventDefault();
        renderCart(true);
      }
      if (event.target.closest('[data-cart-close]')) {
        const drawer = $('[data-cart-drawer]');
        drawer?.classList.remove('is-open');
        drawer?.setAttribute('aria-hidden', 'true');
        lockScroll(false);
      }
      const change = event.target.closest('[data-cart-change]');
      if (change) {
        const row = change.closest('.pp-cart-item');
        const current = Number(row?.querySelector('.pp-cart-item__qty b')?.textContent || 1);
        await changeLine(Number(change.dataset.cartChange), Math.max(0, current + Number(change.dataset.delta)));
        renderCart(false);
      }
      const remove = event.target.closest('[data-cart-remove]');
      if (remove) {
        await changeLine(Number(remove.dataset.cartRemove), 0);
        renderCart(false);
      }
    });

    document.addEventListener('submit', async (event) => {
      const form = event.target.closest('form[action*="/cart/add"]');
      if (!form) return;
      event.preventDefault();
      const button = $('[type="submit"]', form);
      const label = button?.innerHTML;
      if (button) {
        button.disabled = true;
        button.textContent = 'Adding…';
      }
      try {
        const flight = flyProductToCart(form);
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form),
        });
        if (!response.ok) throw new Error(`Cart add failed: ${response.status}`);
        await flight;
        await renderCart(true);
      } catch (err) {
        console.error(err);
        form.submit();
      } finally {
        if (button) {
          button.disabled = false;
          button.innerHTML = label;
        }
      }
    });
  }

  /* ------------------------------------------------------------------------
     3. Product page
     ------------------------------------------------------------------------ */
  function initProductGallery(gallery) {
    const main = $('[data-pdp-main]', gallery);
    const thumbs = $$('[data-media-src]', gallery);
    const counter = $('[data-media-current]', gallery);
    let index = 0;

    const show = (n) => {
      if (!thumbs.length || !main) return;
      index = (n + thumbs.length) % thumbs.length;
      const thumb = thumbs[index];
      main.classList.add('is-changing');
      window.setTimeout(() => {
        main.src = thumb.dataset.mediaSrc;
        main.alt = thumb.dataset.mediaAlt || '';
        main.classList.remove('is-changing');
      }, 120);
      thumbs.forEach((t) => t.classList.remove('is-active'));
      thumb.classList.add('is-active');
      if (counter) counter.textContent = index + 1;
    };

    thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => show(i)));
    $('[data-media-prev]', gallery)?.addEventListener('click', () => show(index - 1));
    $('[data-media-next]', gallery)?.addEventListener('click', () => show(index + 1));

    // Swipe on touch screens.
    let startX = 0;
    gallery.addEventListener('touchstart', (e) => (startX = e.touches[0].clientX), { passive: true });
    gallery.addEventListener(
      'touchend',
      (e) => {
        const dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) > 45) show(index + (dx < 0 ? 1 : -1));
      },
      { passive: true }
    );

    // Full-screen view: the ⌕ button or a click on the main image opens it.
    const lightbox = $('[data-lightbox]');
    const lightboxImage = lightbox?.querySelector('[data-lightbox-image]');
    const zoomButton = $('[data-media-lightbox]', gallery);
    zoomButton?.addEventListener('click', () => {
      if (!lightbox || !lightboxImage) return;
      lightboxImage.src = main.src;
      lightboxImage.alt = main.alt;
      lightbox.classList.add('is-open');
      lightbox.setAttribute('aria-hidden', 'false');
      lockScroll(true);
    });
    main?.addEventListener('click', () => zoomButton?.click());

    // Arrows inside the full-screen view step through the same images.
    if (lightbox && lightboxImage) {
      const stepLightbox = (direction) => (event) => {
        event.preventDefault();
        event.stopPropagation();
        show(index + direction);
        const thumb = thumbs[index];
        if (thumb) {
          lightboxImage.src = thumb.dataset.mediaSrc;
          lightboxImage.alt = thumb.dataset.mediaAlt || '';
        }
      };
      $('[data-lightbox-prev]', lightbox)?.addEventListener('click', stepLightbox(-1));
      $('[data-lightbox-next]', lightbox)?.addEventListener('click', stepLightbox(1));
    }
  }

  function initLightboxClose() {
    $$('[data-lightbox-close]').forEach((button) =>
      button.addEventListener('click', () => {
        const lightbox = button.closest('[data-lightbox]');
        lightbox?.classList.remove('is-open');
        lightbox?.setAttribute('aria-hidden', 'true');
        lockScroll(false);
      })
    );
  }

  // Keep price, image and sticky-bar price in sync with the chosen variant.
  function initVariantSelect(select) {
    select.addEventListener('change', () => {
      const option = select.options[select.selectedIndex];
      const root = select.closest('[data-product-root]');
      const price = select.closest('.pp-pdp__info')?.querySelector('[data-product-price]');
      if (price && option?.dataset.price) price.textContent = option.dataset.price;
      if (option.dataset.image) {
        const main = root?.querySelector('[data-pdp-main]');
        if (main) main.src = option.dataset.image;
      }
      root?.querySelectorAll('[data-sticky-price]').forEach((el) => (el.textContent = option.dataset.price || el.textContent));
    });
  }

  function initStickyAddToCart() {
    // The sticky button simply clicks the real Add to cart button.
    $$('[data-sticky-add]').forEach((button) =>
      button.addEventListener('click', () =>
        button.closest('[data-product-root]')?.querySelector('.pp-pdp__form [type="submit"]')?.click()
      )
    );

    // Show the sticky bar once the real button has scrolled above the viewport.
    const bars = [];
    $$('[data-sticky-atc]').forEach((bar) => {
      const root = bar.closest('[data-product-root]') || document;
      const target = root.querySelector('.pp-pdp__form [type="submit"]');
      if (target) bars.push({ bar, target });
      else bar.classList.add('is-visible'); // fail open rather than hiding it forever
    });
    if (!bars.length) return;

    let ticking = false;
    const update = () => {
      ticking = false;
      bars.forEach(({ bar, target }) => bar.classList.toggle('is-visible', target.getBoundingClientRect().bottom < 0));
    };
    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(update);
        }
      },
      { passive: true }
    );
    window.addEventListener('resize', update);
    update();
  }

  /* ------------------------------------------------------------------------
     4. Cuddle Care Box on product pages
     ------------------------------------------------------------------------ */

  // "Build My Cuddle Care Box" button keeps ?variant= pointed at the selected variant.
  function initCuddleCareButton(cta) {
    const base = cta.getAttribute('data-builder-url') || '/pages/build-your-own-box';
    const handle = cta.getAttribute('data-product-handle') || '';

    const selectedVariant = () =>
      $('[data-variant-select]')?.value || $('.pp-pdp__form [name="id"]')?.value || cta.getAttribute('data-default-variant') || '';

    const sync = () => {
      const id = selectedVariant();
      let url = base + (base.includes('?') ? '&' : '?');
      if (id) url += `variant=${encodeURIComponent(id)}&`;
      cta.href = `${url}product=${encodeURIComponent(handle)}`;
    };

    document.addEventListener('change', (event) => {
      const t = event.target;
      if (t?.matches?.('[data-variant-select], .pp-pdp__form [name="id"]')) sync();
    });
    window.addEventListener('pageshow', sync);
    sync();
  }

  // On desktop the Cuddle Care Box sits under the product images; on smaller
  // screens it goes back to its own place in the page.
  function initCuddleCarePlacement() {
    const gallery = $('[data-pdp-gallery]');
    const box = $('.pp-ccb');
    if (!gallery || !box) return;
    const section = box.closest('[id^="shopify-section-"]');
    if (!section || section === gallery) return;

    const originalParent = section.parentNode;
    const originalNext = section.nextSibling;
    const desktop = window.matchMedia('(min-width: 991px)');
    const place = () => {
      if (desktop.matches) {
        if (section.parentNode !== gallery) gallery.appendChild(section);
      } else if (section.parentNode !== originalParent) {
        originalParent.insertBefore(section, originalNext);
      }
    };
    place();
    if (typeof desktop.addEventListener === 'function') desktop.addEventListener('change', place);
    else if (typeof desktop.addListener === 'function') desktop.addListener(place);
  }

  /* ------------------------------------------------------------------------
     5. Build-your-own-box builder
     ------------------------------------------------------------------------ */
  function initBoxBuilder(builder) {
    const required = Number(builder.dataset.required || 0);
    const choices = $$('[data-box-choice]', builder);
    const slots = $$('[data-box-slot]', builder);
    const count = $('[data-box-count]', builder);
    const status = $('[data-box-status]', builder);
    const submit = $('[data-box-submit]', builder);
    const contents = $('[data-box-contents]', builder);
    const variantIds = $('[data-box-variant-ids]', builder);
    const selected = [];

    const draw = () => {
      choices.forEach((choice) => {
        const active = selected.includes(choice);
        choice.classList.toggle('is-selected', active);
        choice.setAttribute('aria-pressed', String(active));
      });
      slots.forEach((slot, i) => {
        const choice = selected[i];
        slot.classList.toggle('is-filled', Boolean(choice));
        const label = $('small', slot);
        if (label) {
          if (!label.dataset.empty) label.dataset.empty = label.textContent;
          label.textContent = choice ? choice.dataset.productTitle : label.dataset.empty;
        }
      });
      const remaining = required - selected.length;
      if (count) count.textContent = selected.length;
      if (contents) contents.value = selected.map((c) => c.dataset.productTitle).join(' | ');
      if (variantIds) variantIds.value = selected.map((c) => c.dataset.variantId).join(', ');
      if (submit) submit.disabled = selected.length !== required;
      if (status) status.textContent = statusText(remaining);
    };

    // Status messages come from the section settings (with the original wording as fallback).
    const statusText = (remaining) => {
      const d = builder.dataset;
      if (remaining === 0) return d.statusReady || 'Your box is ready to add.';
      if (remaining === 1) return d.statusRemainingOne || 'Choose 1 more product to complete your box.';
      return (d.statusRemaining || 'Choose [count] more products to complete your box.').replace('[count]', remaining);
    };

    choices.forEach((choice) =>
      choice.addEventListener('click', () => {
        const i = selected.indexOf(choice);
        if (i > -1) selected.splice(i, 1);
        else if (selected.length < required) selected.push(choice);
        else {
          // Box is full — give it a little shake.
          builder.classList.remove('pp-byob-limit');
          void builder.offsetWidth;
          builder.classList.add('pp-byob-limit');
          window.setTimeout(() => builder.classList.remove('pp-byob-limit'), 400);
        }
        draw();
      })
    );
    draw();
  }

  // Opening the builder from a product page (?variant= or ?product=) ticks that product.
  function preselectBoxProduct(builder) {
    if (builder.getAttribute('data-preselect-enabled') === 'false') return;
    const params = new URLSearchParams(window.location.search);
    const wantedVariant = (params.get('variant') || '').trim();
    const wantedProduct = (params.get('product') || '').trim().toLowerCase();
    if (!wantedVariant && !wantedProduct) return;

    const choices = $$('[data-box-choice]', builder);
    const notice = $('[data-box-notice]', builder);
    const required = Number(builder.getAttribute('data-required') || 0);
    const showNotice = (text) => {
      if (!notice || !text) return;
      notice.textContent = text;
      notice.hidden = false;
    };

    let match = null;
    if (wantedVariant && /^\d+$/.test(wantedVariant)) {
      match = choices.find(
        (c) =>
          c.getAttribute('data-variant-id') === wantedVariant ||
          (c.getAttribute('data-all-variant-ids') || '').split(',').some((id) => id.trim() === wantedVariant)
      );
    }
    if (!match && wantedProduct) {
      match = choices.find((c) => (c.getAttribute('data-product-handle') || '').toLowerCase() === wantedProduct);
    }
    if (!match || match.disabled) {
      showNotice(builder.getAttribute('data-ineligible-message') || '');
      return;
    }

    // Checked against live state so refresh / back button never double-adds.
    const alreadySelected = match.classList.contains('is-selected') || match.getAttribute('aria-pressed') === 'true';
    const selectedCount = builder.querySelectorAll('[data-box-choice].is-selected').length;
    if (!alreadySelected && selectedCount < required) match.click();

    const template = builder.getAttribute('data-preselect-message') || '';
    showNotice(template.replace('[product]', match.getAttribute('data-product-title') || 'That product'));
    try {
      match.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    } catch (e) {}
  }

  // Phones: sticky progress bar that mirrors the builder's state.
  function initBoxMobileBar(builder) {
    const bar = $('[data-box-mobile-bar]', builder);
    if (!bar || bar.dataset.ready === 'true') return;
    bar.dataset.ready = 'true';

    const required = parseInt(builder.dataset.required, 10) || 0;
    const choices = $$('[data-box-choice]', builder);
    const products = $('.pp-byob__products', builder);
    const realSubmit = $('[data-box-submit]', builder);
    const el = {
      count: $('[data-box-mobile-count]', bar),
      message: $('[data-box-mobile-message]', bar),
      progress: $('[data-box-mobile-progress]', bar),
      fill: $('[data-box-mobile-progress-fill]', bar),
      slots: $$('[data-box-mobile-slot]', bar),
      remaining: $('[data-box-mobile-remaining]', bar),
      helper: $('[data-box-mobile-helper]', bar),
      state: $('[data-box-mobile-state-text]', bar),
      submit: $('[data-box-mobile-submit]', bar),
    };
    const msg = (key, fallback, values = {}) =>
      (bar.dataset[key] || fallback).replace('[count]', values.count).replace('[percent]', values.percent);
    const set = (node, text) => node && (node.textContent = text);

    const sync = () => {
      const count = choices.filter((c) => c.getAttribute('aria-pressed') === 'true').length;
      const filled = Math.min(count, required);
      const remaining = Math.max(required - count, 0);
      const percent = required > 0 ? Math.min(100, Math.round((filled / required) * 100)) : 0;

      set(el.count, String(count));
      el.progress?.setAttribute('aria-valuenow', String(filled));
      if (el.fill) el.fill.style.width = `${percent}%`;
      el.slots.forEach((slot, i) => slot.classList.toggle('is-filled', i < filled));

      const complete = count >= required && count > 0;
      bar.classList.toggle('is-complete', complete);
      if (count === 0) {
        set(el.message, msg('msgStart', 'Start picking your favourites'));
        set(el.remaining, msg('remainingStart', '[count] to go', { count: required }));
        set(el.helper, msg('helperStart', 'Complete your box'));
        set(el.state, msg('stateBuilding', 'Building box'));
      } else if (count < required) {
        set(el.message, count === 1 ? msg('msgOne', '1 favourite is already in your box') : msg('msgMany', '[count] favourites are already in your box', { count }));
        set(el.remaining, msg('remainingPartial', '[count] more', { count: remaining }));
        set(el.helper, msg('helperPartial', 'to complete box'));
        set(el.state, msg('stateFilled', '[percent]% filled', { percent }));
      } else {
        set(el.message, msg('msgComplete', 'Your Cuddle Care Box is complete'));
        set(el.remaining, msg('remainingDone', 'Ready!'));
        set(el.helper, msg('helperDone', 'Box complete'));
        set(el.state, msg('stateComplete', 'Complete ✓'));
      }
      if (el.submit && realSubmit) el.submit.disabled = realSubmit.disabled;
    };

    // The sticky button just presses the real "Add complete box" button.
    el.submit?.addEventListener('click', () => {
      if (!el.submit.disabled && !realSubmit?.disabled) realSubmit?.click();
    });

    // Re-sync whenever a pick changes or the real button is enabled/disabled.
    const observer = new MutationObserver(sync);
    if (products) observer.observe(products, { subtree: true, attributes: true, attributeFilter: ['aria-pressed', 'disabled'] });
    if (realSubmit) observer.observe(realSubmit, { attributes: true, attributeFilter: ['disabled'] });
    builder.addEventListener('click', (event) => {
      if (event.target.closest('[data-box-choice]')) requestAnimationFrame(() => requestAnimationFrame(sync));
    });

    // Only show the bar while the builder is on screen.
    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => entries.forEach((e) => bar.classList.toggle('is-visible', e.isIntersecting)), {
        threshold: 0.01,
      }).observe(builder);
    } else {
      bar.classList.add('is-visible');
    }

    sync();
    requestAnimationFrame(sync);
    setTimeout(sync, 100);
    setTimeout(sync, 400);
  }

  /* ------------------------------------------------------------------------
     6. Testimonial carousel
     ------------------------------------------------------------------------ */
  function initReviewCarousel(carousel) {
    const track = $('[data-review-track]', carousel);
    const cards = $$('.live-note', carousel);
    if (!track || cards.length < 2) return;
    const dotsWrap = $('[data-review-dots]', carousel);
    let timer;

    const perView = () => Math.max(1, Math.round(track.clientWidth / cards[0].getBoundingClientRect().width));
    const pageCount = () => Math.ceil(cards.length / perView());
    const currentPage = () => Math.min(pageCount() - 1, Math.round(track.scrollLeft / track.clientWidth));
    const updateDots = () => dotsWrap?.querySelectorAll('button').forEach((dot, i) => dot.classList.toggle('is-active', i === currentPage()));
    const go = (page) => track.scrollTo({ left: Math.max(0, Math.min(page, pageCount() - 1)) * track.clientWidth, behavior: 'smooth' });
    const step = (direction) => go((currentPage() + direction + pageCount()) % pageCount());
    const drawDots = () => {
      if (!dotsWrap) return;
      dotsWrap.innerHTML = '';
      for (let i = 0; i < pageCount(); i++) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.setAttribute('aria-label', `Show review page ${i + 1}`);
        dot.addEventListener('click', () => go(i));
        dotsWrap.appendChild(dot);
      }
      updateDots();
    };
    const stop = () => clearInterval(timer);
    const start = () => {
      if (carousel.dataset.autoplay === 'true' && !prefersReducedMotion()) {
        stop();
        timer = setInterval(() => step(1), Number(carousel.dataset.interval) || 6000);
      }
    };

    $('[data-review-prev]', carousel)?.addEventListener('click', () => step(-1));
    $('[data-review-next]', carousel)?.addEventListener('click', () => step(1));
    track.addEventListener('scroll', () => requestAnimationFrame(updateDots), { passive: true });
    carousel.addEventListener('mouseenter', stop);
    carousel.addEventListener('mouseleave', start);
    carousel.addEventListener('focusin', stop);
    carousel.addEventListener('focusout', start);
    let resizeTimer;
    window.addEventListener(
      'resize',
      () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(drawDots, 120);
      },
      { passive: true }
    );
    drawDots();
    start();
  }

  /* ------------------------------------------------------------------------
     7. Welcome popup (Theme settings → Popups and cart)
     ------------------------------------------------------------------------ */
  function initPopup() {
    const popup = $('[data-site-popup]');
    if (!popup) return;
    const frequency = popup.dataset.frequency;
    const key = 'pp_popup_seen';
    const now = Date.now();
    const seenAt = Number(localStorage.getItem(key) || 0);
    const seenThisSession = sessionStorage.getItem(key);
    const shouldShow =
      frequency === 'always' || (frequency === 'session' && !seenThisSession) || (frequency === '7days' && now - seenAt > 604800000);

    if (shouldShow) {
      setTimeout(() => {
        popup.classList.add('is-open');
        popup.setAttribute('aria-hidden', 'false');
        if (frequency === 'session') sessionStorage.setItem(key, '1');
        if (frequency === '7days') localStorage.setItem(key, String(now));
      }, Number(popup.dataset.delay || 4000));
    }
    $$('[data-popup-close]', popup).forEach((button) =>
      button.addEventListener('click', () => {
        popup.classList.remove('is-open');
        popup.setAttribute('aria-hidden', 'true');
      })
    );
  }

  /* ------------------------------------------------------------------------
     8. Product card background tint
        Samples the dominant colour of each product's packaging photo and
        gives its card a soft pastel of that colour. If a photo can't be
        sampled, the card keeps its normal background.
     ------------------------------------------------------------------------ */
  function initCardTint() {
    const CACHE_KEY = 'ppCardAutoColorCache_v1';
    let cache = {};
    const pending = {};
    try {
      cache = JSON.parse(sessionStorage.getItem(CACHE_KEY)) || {};
    } catch (e) {
      cache = {};
    }
    const saveCache = () => {
      try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(cache));
      } catch (e) {}
    };

    const toHsl = (r, g, b) => {
      r /= 255;
      g /= 255;
      b /= 255;
      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      const l = (max + min) / 2;
      const d = max - min;
      if (d === 0) return { h: 0, s: 0, l };
      const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      let h;
      if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
      else if (max === g) h = (b - r) / d + 2;
      else h = (r - g) / d + 4;
      return { h: h * 60, s, l };
    };

    const dominantHue = (img) => {
      const size = 48;
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, size, size);
      const data = ctx.getImageData(0, 0, size, size).data; // throws if the image is cross-origin
      const BINS = 24;
      const bins = new Array(BINS).fill(0);
      for (let i = 0; i < data.length; i += 4) {
        if (data[i + 3] < 200) continue; // transparent background
        const hsl = toHsl(data[i], data[i + 1], data[i + 2]);
        if (hsl.l > 0.9 || hsl.l < 0.1 || hsl.s < 0.15) continue; // white / black / grey
        bins[Math.floor(hsl.h / (360 / BINS)) % BINS] += hsl.s;
      }
      let best = -1;
      let weight = 0;
      bins.forEach((w, i) => {
        if (w > weight) {
          weight = w;
          best = i;
        }
      });
      return best === -1 ? null : Math.round((best + 0.5) * (360 / BINS));
    };

    const resolve = (src, callback) => {
      if (Object.prototype.hasOwnProperty.call(cache, src)) return callback(cache[src]);
      if (pending[src]) return pending[src].push(callback);
      pending[src] = [callback];
      const settle = (color) => {
        cache[src] = color;
        saveCache();
        const callbacks = pending[src] || [];
        delete pending[src];
        callbacks.forEach((cb) => cb(color));
      };
      const probe = new Image();
      probe.crossOrigin = 'anonymous';
      probe.onload = () => {
        let color = null;
        try {
          const hue = dominantHue(probe);
          if (hue !== null) color = `hsl(${hue}, 58%, 94%)`;
        } catch (e) {}
        settle(color);
      };
      probe.onerror = () => settle(null);
      probe.src = src;
    };

    const tint = (media) => {
      if (media.dataset.ppAutoColored) return;
      const src = media.querySelector('img')?.getAttribute('src');
      if (!src) return;
      media.dataset.ppAutoColored = 'pending';
      resolve(src, (color) => {
        if (color) {
          media.style.backgroundColor = color;
          media.dataset.ppAutoColored = '1';
        } else {
          delete media.dataset.ppAutoColored;
        }
      });
    };

    const scan = (root) => (root?.querySelectorAll ? root : document).querySelectorAll('.pp-card__media').forEach(tint);
    scan(document);
    document.addEventListener('shopify:section:load', (event) => scan(event.target));
    if ('MutationObserver' in window) {
      new MutationObserver((mutations) => {
        mutations.forEach((m) =>
          m.addedNodes.forEach((node) => {
            if (node.nodeType !== 1) return;
            if (node.matches?.('.pp-card__media')) tint(node);
            if (node.querySelectorAll) scan(node);
          })
        );
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  /* ------------------------------------------------------------------------
     9. Ambient motion — scroll parallax for the floating background shapes
        (snippets/pp-ambient-motion.liquid reads --pp-scroll-shift)
     ------------------------------------------------------------------------ */
  function initScrollParallax() {
    if (prefersReducedMotion()) return;
    let ticking = false;
    const update = () => {
      ticking = false;
      if (motionTurnedOff()) return;
      const shift = Math.max(-60, Math.min(60, window.scrollY * -0.04));
      document.documentElement.style.setProperty('--pp-scroll-shift', `${shift}px`);
    };
    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(update);
        }
      },
      { passive: true }
    );
    update();
  }

  /* ------------------------------------------------------------------------
     10. Page-ready behaviour
     ------------------------------------------------------------------------ */
  function initMobileMenu() {
    const button = $('[data-menu-toggle]');
    const menu = $('[data-mobile-menu]');
    if (button && menu) button.addEventListener('click', () => menu.classList.toggle('is-open'));
  }

  // Header compacts slightly once the page is scrolled.
  function initHeaderScroll() {
    const header = $('[data-header]');
    const update = () => header?.classList.toggle('is-scrolled', window.scrollY > 20);
    update();
    window.addEventListener('scroll', update, { passive: true });
  }

  // Soft fade-up as sections enter the viewport.
  function initScrollReveal() {
    const targets = $$('.pp-reveal,.pp-section,.pp-why,.pp-closing,.pp-collection-hero,.pp-pdp__top,.pp-care-guide,.pp-recommendations,.live-hero');
    targets.forEach((el) => el.classList.add('pp-reveal'));
    if (!('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        }),
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    targets.forEach((el) => observer.observe(el));
  }

  // Product art drifts a little with the mouse pointer.
  function initPointerParallax() {
    const zones = $$('[data-float-zone],.pp-hero__products,.pp-spotlight__visual,.pp-treat__media');
    window.addEventListener(
      'pointermove',
      (e) => {
        const x = e.clientX / window.innerWidth - 0.5;
        const y = e.clientY / window.innerHeight - 0.5;
        zones.forEach((zone) => {
          zone.style.setProperty('--mx', `${x * 10}px`);
          zone.style.setProperty('--my', `${y * 8}px`);
        });
      },
      { passive: true }
    );
  }

  function initQuantityButtons() {
    $$('[data-qty]').forEach((wrapper) => {
      const input = $('input', wrapper);
      $('[data-qty-minus]', wrapper)?.addEventListener('click', () => (input.value = Math.max(1, (parseInt(input.value) || 1) - 1)));
      $('[data-qty-plus]', wrapper)?.addEventListener('click', () => (input.value = (parseInt(input.value) || 1) + 1));
    });
  }

  // Shop-all page: search box + product-type filter chips.
  function initShopFilters() {
    const search = $('[data-product-search]');
    const buttons = $$('[data-filter]');
    const items = $$('[data-product-item]');
    const count = $('[data-result-count]');
    let active = 'all';
    const apply = () => {
      const query = (search?.value || '').trim().toLowerCase();
      let visible = 0;
      items.forEach((item) => {
        const type = (item.dataset.productType || '').toLowerCase();
        const title = (item.dataset.productTitle || '').toLowerCase();
        const show = (active === 'all' || type.includes(active)) && (!query || title.includes(query));
        item.hidden = !show;
        if (show) visible++;
      });
      if (count) count.textContent = `${visible} ${count.dataset.label || 'products'}`;
    };
    search?.addEventListener('input', apply);
    buttons.forEach((button) =>
      button.addEventListener('click', () => {
        active = button.dataset.filter;
        buttons.forEach((b) => b.classList.remove('is-active'));
        button.classList.add('is-active');
        apply();
      })
    );
  }

  /* ------------------------------------------------------------------------
     11. Boot
         The first group runs as soon as this deferred script executes; the
         page-ready group runs on DOMContentLoaded. This mirrors the original
         load order (builder before preselection, gallery before placement).
     ------------------------------------------------------------------------ */
  initCart();
  $$('[data-pdp-gallery]').forEach(initProductGallery);
  initLightboxClose();
  $$('[data-variant-select]').forEach(initVariantSelect);
  initStickyAddToCart();
  initPopup();
  $$('[data-review-carousel]').forEach(initReviewCarousel);
  $$('[data-build-box]').forEach((builder) => {
    initBoxBuilder(builder);
    preselectBoxProduct(builder);
    initBoxMobileBar(builder);
  });
  // Theme editor: re-initialise the builder's mobile bar when its section reloads.
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll?.('[data-build-box]').forEach(initBoxMobileBar);
  });
  $$('[data-ccb-cta]').forEach(initCuddleCareButton);
  initScrollParallax();
  initCardTint();
  initCuddleCarePlacement();

  const onReady = () => {
    initMobileMenu();
    initHeaderScroll();
    initScrollReveal();
    initPointerParallax();
    initQuantityButtons();
    initShopFilters();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', onReady);
  else onReady();
})();
